/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { PropsWithChildren, useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { MuiXLicense } from "@myplant-io/mui";
import { CssBaseline, StyledEngineProvider } from "@mui/material";
import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  defaultDataIdFromObject,
  InMemoryCache,
} from "@apollo/client";

import "./index.css";

import Layout from "./pages/layout/Layout";
import Chat from "./pages/chat/Chat";
import { AppStateProvider } from "./state/App";
import { RouteNames } from "./RouteNames";
import { LandingPage } from "./pages/LandingPage";
import { useIntl } from "react-intl";
import { IntlProvider } from "./state/Intl";
import { LoginPage } from "./pages/LoginPage";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { SnackbarProvider, useSnackbar } from "@myplant-io/snackbar";
import { ApiClientProvider } from "./state/Api";
import { TrackingProvider } from "./state/Tracking";
import { LocalizationProvider } from "./state/Localization";
import { ThemeProvider } from "./state/Theme";
import { AllAppsPageView } from "@myplant-io/app-launcher";
import { TextToSpeechProvider } from "./state/TextToSpeech";
import KnowledgeBase from "./pages/knowledge-base/KnowledgeBase";
import { ModeImport } from "./pages/knowledge-base/ModeImport";
import { LogoutPage } from "./pages/LogoutPage";
// @ts-expect-error vite
import { registerSW } from "virtual:pwa-register";
import { commonMessages } from "./commonMessages";
import Memory from "./pages/memory/Memory";
import { ModeStore } from "./pages/mode-store/ModeStore";
import "./utils/consoleOverrides";
import { KioskPage } from "./pages/Kiosk";
import { UserManagementPage } from "./pages/user-management/UserManagement";
import { TeamsAuthComplete } from "./pages/TeamsAuthComplete";
import { TeamsProvider } from "./state/Teams";
import { useAuth } from "@myplant-io/hooks";
import { API_BASE_URL } from "./constants";

declare module "@mui/material/styles/createPalette" {
  interface TypeBackground {
    placeholder: string;
    paperLevel2: string;
    accent: string;
  }
}

declare module "@mui/x-data-grid-premium" {
  interface ToolbarPropsOverrides {
    onRestore: () => void;
  }
}

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    "2lg": true; // additional breakpoint
    xl: true;
  }
}

export function useColorScheme() {
  const matcher = window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : false;
  const [isDark, setIsDark] = useState(matcher ? matcher.matches : false);

  useEffect(() => {
    if (!matcher) {
      return;
    }

    const listener = (e: MediaQueryListEvent) => {
      setIsDark(e.matches);
    };
    matcher.addEventListener("change", listener);
    return () => matcher.removeEventListener("change", listener);
  }, [matcher]);
  return isDark;
}

const PrivateRoute = ({ children }: PropsWithChildren<unknown>) => {
  const location = useLocation();
  const { data, isFetched } = useAuth(API_BASE_URL);

  if (!isFetched) {
    return null;
  }

  if (!data) {
    return <Navigate to={RouteNames.LOGIN} state={{ from: location }} />;
  }
  return <>{children}</>;
};

export const AllAppsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <div className="p-4 px-4 sm:px-6">
      <AllAppsPageView
        sx={{ flex: 1 }}
        locationPathname={location.pathname}
        wsBaseUrl="/ws"
        onNavigate={(path) => navigate(path)}
      />
    </div>
  );
};

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path={RouteNames.LOGIN} element={<LoginPage />} />
      <Route path={RouteNames.LOGOUT} element={<LogoutPage />} />
      {/* Intentionally outside the authenticated <Layout>: this is the OAuth
          callback page opened in the Teams popup. It must be reachable before a
          session exists so that `authentication.notifySuccess()` can close the
          popup and return the token to the opener. Do not move inside <Layout>. */}
      <Route
        path={RouteNames.TEAMS_AUTH_COMPLETE}
        element={<TeamsAuthComplete />}
      />
      <Route index element={<LandingPage />} />
      <Route element={<Layout />}>
        <Route path={RouteNames.APPS} element={<AllAppsPage />} />
        <Route path={RouteNames.KIOSK} element={<KioskPage />} />
        <Route
          path={RouteNames.CHAT}
          element={
            <PrivateRoute>
              <Chat />
            </PrivateRoute>
          }
        />
        <Route
          path={RouteNames.KNOWLEDGE_BASE}
          element={
            <PrivateRoute>
              <KnowledgeBase />
            </PrivateRoute>
          }
        />
        <Route
          path={RouteNames.MEMORY}
          element={
            <PrivateRoute>
              <Memory />
            </PrivateRoute>
          }
        />
        <Route
          path={RouteNames.MODE_STORE}
          element={
            <PrivateRoute>
              <ModeStore />
            </PrivateRoute>
          }
        />
        <Route
          path={RouteNames.MODE_IMPORT}
          element={
            <PrivateRoute>
              <ModeImport />
            </PrivateRoute>
          }
        />
        <Route
          path={RouteNames.USER}
          element={
            <PrivateRoute>
              <UserManagementPage />
            </PrivateRoute>
          }
        />
      </Route>
    </>
  )
);

function UpdateServiceWorker() {
  const { formatMessage: t } = useIntl();
  const { showInfoMessage } = useSnackbar();
  useEffect(() => {
    let i: ReturnType<typeof setInterval> | undefined = undefined;
    const updateSW = registerSW({
      onRegistered(r: ServiceWorkerRegistration) {
        if (r) {
          i = setInterval(() => {
            r.update();
          }, 1000 * 60);
        }
      },
      onNeedRefresh() {
        showInfoMessage({
          message: t(commonMessages.newUpdateAvailable),
          action: t(commonMessages.reload),
          handleAction: () => {
            updateSW();
          },
        });
      },
    });
    return () => {
      if (i) {
        clearInterval(i);
      }
    };
  }, [showInfoMessage, t]);
  return null;
}

export const createClient = () =>
  new ApolloClient({
    link: createHttpLink({
      uri: "/ws/graphql",
      credentials: "same-origin",
    }),
    cache: new InMemoryCache({
      dataIdFromObject: (object) => {
        return defaultDataIdFromObject(object);
      },
    }),
  });

const client = createClient();
const queryClient = new QueryClient();

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppStateProvider>
        <TeamsProvider>
          <StyledEngineProvider injectFirst>
            <SnackbarProvider>
              <IntlProvider>
                <ThemeProvider>
                  <MuiXLicense />
                  <CssBaseline />
                  <ApiClientProvider>
                    <LocalizationProvider>
                      <ApolloProvider client={client}>
                        <TrackingProvider>
                          <TextToSpeechProvider>
                            <UpdateServiceWorker />
                            <RouterProvider
                              router={router}
                              future={{ v7_startTransition: true }}
                            />
                          </TextToSpeechProvider>
                        </TrackingProvider>
                      </ApolloProvider>
                    </LocalizationProvider>
                  </ApiClientProvider>
                </ThemeProvider>
              </IntlProvider>
            </SnackbarProvider>
          </StyledEngineProvider>
        </TeamsProvider>
      </AppStateProvider>
      <ReactQueryDevtools />
    </QueryClientProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
